import { AbstractControl, FormGroup, ValidationErrors, ValidatorFn } from '@angular/forms';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';

export class FormUtils {

  public static arraysEquals(a: any[], b: any[]): boolean {
    return a?.length === b?.length && a.every(elementInA => b.some(elementInB => elementInA === elementInB));
  }

  public static clearFormGroup(formGroup: FormGroup) {
    for (const key of Object.keys(formGroup.controls)) {
      formGroup.removeControl(key);
    }
    formGroup.markAsPristine();
    formGroup.markAsUntouched();
  }

  public static checkForErrors(formGroup: FormGroup): number {
    let errors = 0;
    if (formGroup && formGroup.invalid) {
      const stack: AbstractControl[] = [formGroup];
      while (stack.length > 0) {
        const current = stack.pop();
        if (current['controls']) {
          stack.push(...Object.values((current as FormGroup).controls));
        }
        if (!!current.errors) {
          errors++;
        }
      }
    }
    return errors;
  }

  public static checkDirty(formGroup: FormGroup): boolean {
    let formIsDirty = false;
    if (formGroup) {
      for (const control of Object.values(formGroup['controls'])) {
        if (control['controls']) {
          formIsDirty = this.checkDirty(control as FormGroup);
        } else if (control.dirty) {
          formIsDirty = true;
          control.markAsDirty();
        }
      }
    }
    return formIsDirty;
  }

  public static calculateChangesBetween(initialValues: any, currentValues: any): Change[] {
    return this.recursiveChangeCheck('', [], initialValues, currentValues);
  }

  private static recursiveChangeCheck(
    path: string,
    changes: Change[],
    initialValues: any,
    currentValues: any,
  ): Change[] {
    const keys = new Set<string>();
    if (initialValues) {
      Object.keys(initialValues).forEach(key => keys.add(key));
    }
    if (currentValues) {
      Object.keys(currentValues).forEach(key => keys.add(key));
    }
    for (const key of Array.from(keys)) {
      const initialValue = initialValues?.[key];
      const currentValue = currentValues?.[key];
      if (this.isObject(initialValue || this.isObject(currentValue))) {
        this.recursiveChangeCheck(path + key, changes, initialValue, currentValue);
      } else if (Array.isArray(initialValue) || Array.isArray(currentValue)) {
        if (!this.arrayEquals(initialValue, currentValue)) {
          changes.push({
            path, key, initialValue, currentValue, type: this.typeOfChange(initialValue, currentValue),
          });
        }
      } else if (initialValue !== currentValue) {
        changes.push({
          path, key, initialValue, currentValue, type: this.typeOfChange(initialValue, currentValue),
        });
      }
    }
    return changes;
  }

  private static isObject(obj: any): boolean {
    return !!obj && typeof obj === 'object' && !Array.isArray(obj);
  }

  private static typeOfChange(initialValue: any, currentValue: any): 'ADD' | 'REMOVE' | 'REPLACE' {
    if (!initialValue && !!currentValue) {
      return 'ADD';
    }
    if (!!initialValue && !currentValue) {
      return 'REMOVE';
    }
    return 'REPLACE';
  }

  private static arrayEquals(initial: any, current: any): boolean {
    if (initial === current) {
      return true;
    }
    if (initial.length !== current.length) {
      return false;
    }
    for (let i = 0; i < initial.length; i++) {
      if (initial[i] !== current[i]) {
        return false;
      }
    }
    return true;
  }

  public static validatorBothPositiveNumberOrBothNull(a: string, b: string, mandatory: boolean = false): ValidatorFn {
    return (group: AbstractControl): ValidationErrors | null => {
      const ctrlA = group.get(a);
      const ctrlB = group.get(b);
      const valueA = ctrlA?.value;
      const valueB = ctrlB?.value;
      this.validatorBothPositiveNumberOrBothNullInternal(ctrlA, valueA, valueB, mandatory);
      this.validatorBothPositiveNumberOrBothNullInternal(ctrlB, valueB, valueA, mandatory);
      return null;
    };
  }

  public static onTrueChanges(formGroup: FormGroup, destroy$: Subject<boolean>, callback: () => void) {
    let lastVal = Object.assign({}, formGroup.value);
    formGroup
      .valueChanges
      .pipe(takeUntil(destroy$))
      .subscribe(newVal => {
        const changes = this.calculateChangesBetween(lastVal, newVal);
        if (changes.length > 0) {
          lastVal = Object.assign({}, newVal);
          callback();
        }
      });
  }

  private static validatorBothPositiveNumberOrBothNullInternal(ctrl: AbstractControl, value: number, other: number, mandatory: boolean) {
    const errors = {};
    let hasErrors = false;
    if (!!value && value <= 0) {
      errors['invalid'] = true;
      hasErrors = true;
    }
    if (mandatory && !value) {
      errors['required'] = true;
      hasErrors = true;
    }
    if (!value && !!other) {
      errors['missing'] = true;
      hasErrors = true;
    }
    if (hasErrors) {
      ctrl.setErrors(errors);
    } else {
      ctrl.setErrors(null);
    }
  }

}
export interface Change {
  path: string;
  key: string;
  initialValue: any;
  currentValue: any;
  type: 'ADD' | 'REMOVE' | 'REPLACE';
}
